export const PARTICIPANT_PROGRAM_MEDIA_SOURCE_KIND = 'participant_stage' as const;

export type ParticipantProgramMediaSourceKind = typeof PARTICIPANT_PROGRAM_MEDIA_SOURCE_KIND;

export interface ParticipantProgramMediaTracks {
  audio: boolean;
  video: boolean;
}

export interface ParticipantProgramMediaPayload {
  roomId: string;
  sessionId: string;
  alias: string;
  path: string;
  sourceKind: ParticipantProgramMediaSourceKind;
  tracks: ParticipantProgramMediaTracks;
  updatedAt: number;
}

const ROOM_ID_PATTERN = /^[a-zA-Z0-9_-]+$/;
const SESSION_ID_PATTERN = /^[a-zA-Z0-9-]+$/;

export function getParticipantProgramMediaPath(roomId: string, sessionId: string): string {
  return `room-stage/${roomId}/participant/${sessionId}`;
}

export function getParticipantProgramMediaWhipUrl(roomId: string, sessionId: string): string {
  return `/api/whip-proxy/${getParticipantProgramMediaPath(roomId, sessionId)}/whip`;
}

export function getParticipantProgramMediaWhepUrl(path: string): string {
  return `/api/whep-proxy/${path}/whep`;
}

export function isParticipantProgramMediaPath(value: string): boolean {
  const [prefix, roomId, participantSegment, sessionId, ...rest] = value.split('/');
  return (
    prefix === 'room-stage' &&
    participantSegment === 'participant' &&
    rest.length === 0 &&
    ROOM_ID_PATTERN.test(roomId ?? '') &&
    SESSION_ID_PATTERN.test(sessionId ?? '')
  );
}

export function parseParticipantProgramMediaPath(value: string): {
  roomId: string;
  sessionId: string;
} | null {
  if (!isParticipantProgramMediaPath(value)) return null;
  const [, roomId, , sessionId] = value.split('/');
  return { roomId, sessionId };
}

export function isParticipantProgramMediaPayload(value: unknown): value is ParticipantProgramMediaPayload {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.roomId === 'string' &&
    typeof record.sessionId === 'string' &&
    typeof record.alias === 'string' &&
    typeof record.path === 'string' &&
    record.path === getParticipantProgramMediaPath(record.roomId, record.sessionId) &&
    record.sourceKind === PARTICIPANT_PROGRAM_MEDIA_SOURCE_KIND &&
    typeof record.updatedAt === 'number' &&
    Number.isFinite(record.updatedAt) &&
    typeof record.tracks === 'object' &&
    record.tracks !== null &&
    typeof (record.tracks as Record<string, unknown>).audio === 'boolean' &&
    typeof (record.tracks as Record<string, unknown>).video === 'boolean'
  );
}
